import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { autoUpdate } from "@floating-ui/react";

export function BackdropHighlight({
  target,
  padding = 5,
  preventOutsideClick = false,
}: {
  target: Element;
  padding?: number;
  preventOutsideClick?: boolean;
}) {
  const [floatingEl, setFloatingEl] = useState<HTMLDivElement | null>(null);

  const [cutout, setCutout] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    path: string;
  } | null>(null);

  const updateHighlight = useCallback(() => {
    const box = target.getBoundingClientRect();
    // const borderRadius = Number.parseFloat(getComputedStyle(el).borderRadius);
    const borderRadius = 10;

    const cutOutPath = makeOverlayPath({
      width: box.width + 2 * padding,
      height: box.height + 2 * padding,
      x: box.x - padding,
      y: box.y - padding,
      r: borderRadius,
    });

    setCutout({
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
      path: cutOutPath,
    });
  }, [target, padding]);

  useEffect(() => {
    updateHighlight();
    const resizeListener = () => updateHighlight();
    window.addEventListener("resize", resizeListener);

    return () => {
      window.removeEventListener("resize", resizeListener);
    };
  }, [updateHighlight]);

  useEffect(() => {
    if (!floatingEl) return;
    const cleanup = autoUpdate(target, floatingEl, () => updateHighlight(), {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true,
    });

    return () => {
      cleanup();
    };
  }, [target, floatingEl, updateHighlight]);

  return (
    cutout &&
    createPortal(
      <div
        id="pyne-backdrop"
        ref={setFloatingEl}
        style={{
          position: "fixed",
          zIndex: "var(--pyne-layer-base)",
          left: "0px",
          top: "0px",
          height: "100%",
          width: "100%",
          clipPath: `path('${cutout.path}')`,
          userSelect: "none",
          pointerEvents: preventOutsideClick ? "auto" : "none",
          transformOrigin: `${cutout.x + cutout.width / 2}px ${cutout.y + cutout.height / 2}px`,
        }}
      ></div>,
      document.body
    )
  );
}

// Create a path for an overlay that cuts out a rectangle with rounded corners
// Source: https://github.com/shepherd-pro/shepherd/blob/01747712fabc0a193323e3d2633724ed469d731c/src/js/utils/overlay-path.js
function makeOverlayPath({
  width,
  height,
  x = 0,
  y = 0,
  r = 0,
}: {
  width: number;
  height: number;
  x?: number;
  y?: number;
  r?: number | { topLeft: number; topRight: number; bottomRight: number; bottomLeft: number };
}) {
  const { innerWidth: w, innerHeight: h } = window;
  const {
    topLeft = 0,
    topRight = 0,
    bottomRight = 0,
    bottomLeft = 0,
  } = typeof r === "number" ? { topLeft: r, topRight: r, bottomRight: r, bottomLeft: r } : r;

  return `M${w},${h}\
H0\
V0\
H${w}\
V${h}\
Z\
M${x + topLeft},${y}\
a${topLeft},${topLeft},0,0,0-${topLeft},${topLeft}\
V${height + y - bottomLeft}\
a${bottomLeft},${bottomLeft},0,0,0,${bottomLeft},${bottomLeft}\
H${width + x - bottomRight}\
a${bottomRight},${bottomRight},0,0,0,${bottomRight}-${bottomRight}\
V${y + topRight}\
a${topRight},${topRight},0,0,0-${topRight}-${topRight}\
Z`;
}
